import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openNotification } from '../../UI';
import { File as GQLFile } from '../../generated/graphql';
import { useFile } from '../queries';

interface UseViewFileOptions {
  shouldRefreshDownloadURL?: boolean;
}

/**
 * Opens a file in a new browser tab, refreshing its (short-lived) signed
 * download URL first. The blank tab is opened synchronously, before the
 * refetch await, so it isn't blocked as a popup.
 */
export const useViewFile = (
  tenantID: string,
  file: Pick<GQLFile, 'id' | 'downloadURL'>,
  { shouldRefreshDownloadURL = true }: UseViewFileOptions = {}
) => {
  const { t: tFile } = useTranslation('file');
  const { fileQuery } = useFile(tenantID, file.id, { enabled: false, retry: false });
  const [isViewing, setIsViewing] = useState(false);

  const viewFile = async () => {
    setIsViewing(true);
    const newTab = window.open('about:blank', '_blank');
    if (newTab) newTab.opener = null;
    try {
      const freshFileQuery = shouldRefreshDownloadURL ? await fileQuery.refetch() : undefined;
      const freshURL = shouldRefreshDownloadURL ? freshFileQuery?.data?.downloadURL : file.downloadURL;
      const resolved = shouldRefreshDownloadURL ? Boolean(freshFileQuery?.isSuccess && freshURL) : Boolean(freshURL);
      if (resolved && freshURL) {
        if (newTab) newTab.location.href = freshURL;
      } else {
        newTab?.close();
        openNotification({ type: 'error', description: tFile('error-unable-to-download') });
      }
    } catch {
      newTab?.close();
      openNotification({ type: 'error', description: tFile('error-unable-to-download') });
    } finally {
      setIsViewing(false);
    }
  };

  return { viewFile, isViewing };
};
